import React, { useState, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Prompt } from './promptsData';

interface PromptFillerViewProps {
  prompt: Prompt;
  onClose: () => void;
  onBack?: () => void;
  onSubmit: (filledPrompt: string, files?: File[], systemPrefix?: string) => void;
}

const PromptFillerView: React.FC<PromptFillerViewProps> = ({
  prompt,
  onClose,
  onBack,
  onSubmit,
}) => {
  // Initialize values state with empty strings for each variable
  const [values, setValues] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    prompt.variables.forEach((v) => {
      initial[v.key] = '';
    });
    return initial;
  });

  // File attachment state
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [dragOverField, setDragOverField] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fieldDragCounterRef = useRef<Record<string, number>>({});

  const handleInputChange = useCallback((key: string, value: string) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      setAttachedFiles((prev) => [...prev, ...newFiles]);
    }
    // Reset input to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = (index: number) => {
    const fileToRemove = attachedFiles[index];
    if (fileToRemove) {
      // Remove file references from all fields that use this file
      setFieldFileRefs((prev) => {
        const newRefs = { ...prev };
        Object.keys(newRefs).forEach((fieldKey) => {
          if (newRefs[fieldKey] === fileToRemove.name) {
            delete newRefs[fieldKey];
            // Also clear the field value
            setValues((prevValues) => ({ ...prevValues, [fieldKey]: '' }));
          }
        });
        return newRefs;
      });
    }
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // State for dragging attached files to fields
  const [draggingFileIndex, setDraggingFileIndex] = useState<number | null>(null);

  // Handle drag start from attached file
  const handleAttachedFileDragStart = (e: React.DragEvent, fileIndex: number) => {
    setDraggingFileIndex(fileIndex);
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('text/plain', `file-index:${fileIndex}`);
  };

  const handleAttachedFileDragEnd = () => {
    setDraggingFileIndex(null);
    setDragOverField(null);
  };

  // Per-field drag and drop handlers (for attached files)
  const handleFieldDragEnter = (e: React.DragEvent, fieldKey: string) => {
    e.preventDefault();
    e.stopPropagation();
    // Only highlight if we're dragging an attached file
    if (draggingFileIndex !== null) {
      if (!fieldDragCounterRef.current[fieldKey]) {
        fieldDragCounterRef.current[fieldKey] = 0;
      }
      fieldDragCounterRef.current[fieldKey]++;
      setDragOverField(fieldKey);
    }
  };

  const handleFieldDragLeave = (e: React.DragEvent, fieldKey: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (!fieldDragCounterRef.current[fieldKey]) {
      fieldDragCounterRef.current[fieldKey] = 0;
    }
    fieldDragCounterRef.current[fieldKey]--;
    if (fieldDragCounterRef.current[fieldKey] === 0) {
      setDragOverField(null);
    }
  };

  const handleFieldDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggingFileIndex !== null) {
      e.dataTransfer.dropEffect = 'copy';
    }
  };

  // Track which fields have file references (fieldKey -> fileName)
  const [fieldFileRefs, setFieldFileRefs] = useState<Record<string, string>>({});

  const handleFieldDrop = (e: React.DragEvent, fieldKey: string) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverField(null);
    fieldDragCounterRef.current[fieldKey] = 0;

    // Check if we're dropping an attached file
    const data = e.dataTransfer.getData('text/plain');
    if (data.startsWith('file-index:')) {
      const fileIndex = parseInt(data.replace('file-index:', ''), 10);
      const file = attachedFiles[fileIndex];
      if (file) {
        // Set the file reference for this field
        setFieldFileRefs((prev) => ({ ...prev, [fieldKey]: file.name }));
        // Set the value as the file reference
        handleInputChange(fieldKey, `Conteúdo em <${file.name}>`);
      }
    }
    setDraggingFileIndex(null);
  };

  // Remove file reference from a field
  const handleRemoveFieldFileRef = (fieldKey: string) => {
    setFieldFileRefs((prev) => {
      const newRefs = { ...prev };
      delete newRefs[fieldKey];
      return newRefs;
    });
    handleInputChange(fieldKey, '');
  };

  // Extract default value from placeholder (first example after "Ex: ")
  const getDefaultValue = useCallback((placeholder: string) => {
    const match = placeholder.match(/^Ex:\s*(.+?)(?:,|$)/);
    return match ? match[1].trim() : placeholder;
  }, []);

  // Build the filled prompt by replacing placeholders
  const filledPrompt = useMemo(() => {
    let result = prompt.template;
    prompt.variables.forEach((v) => {
      const value = values[v.key]?.trim() || getDefaultValue(v.placeholder);
      result = result.replace(new RegExp(`\\{\\{${v.key}\\}\\}`, 'g'), value);
    });
    // Add file references at the end if files are attached
    if (attachedFiles.length > 0) {
      const fileRefs = attachedFiles.map(f => `Conteúdo em <${f.name}>`).join('\n');
      result = result + '\n\n' + fileRefs;
    }
    return result;
  }, [prompt.template, prompt.variables, values, attachedFiles]);

  // Check if all required fields are filled
  const allFieldsFilled = useMemo(() => {
    return prompt.variables.every((v) => values[v.key]?.trim().length > 0);
  }, [prompt.variables, values]);

  const handleSubmit = () => {
    onSubmit(filledPrompt, attachedFiles.length > 0 ? attachedFiles : undefined, prompt.systemPrefix);
  };

  // Render template with highlighted variables showing current values
  const renderTemplatePreview = () => {
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    const regex = /\{\{(\w+)\}\}/g;
    let match;

    while ((match = regex.exec(prompt.template)) !== null) {
      if (match.index > lastIndex) {
        parts.push(
          <span key={`text-${lastIndex}`}>
            {prompt.template.slice(lastIndex, match.index)}
          </span>
        );
      }

      const varKey = match[1];
      const variable = prompt.variables.find((v) => v.key === varKey);
      const value = values[varKey]?.trim();
      const defaultVal = variable ? getDefaultValue(variable.placeholder) : varKey;

      parts.push(
        <HighlightedVar key={`var-${match.index}`} $filled={!!value} title={variable?.label || varKey}>
          {value || defaultVal}
        </HighlightedVar>
      );

      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < prompt.template.length) {
      parts.push(
        <span key={`text-${lastIndex}`}>
          {prompt.template.slice(lastIndex)}
        </span>
      );
    }

    return parts;
  };

  return createPortal(
    <FillerOverlay>
      <FillerContainer>
        <FillerHeader>
          <HeaderLeft>
            <BackButton onClick={onBack || onClose} aria-label="Voltar">
              <ArrowBackIcon />
            </BackButton>
            <HeaderInfo>
              <HeaderTitle>{prompt.title}</HeaderTitle>
              {prompt.description && <HeaderDescription>{prompt.description}</HeaderDescription>}
            </HeaderInfo>
          </HeaderLeft>
          <CloseButton onClick={onClose} aria-label="Fechar">
            <CloseIcon />
          </CloseButton>
        </FillerHeader>

        <FillerBody>
          <LeftColumn>
            <FieldsContainer>
              {prompt.variables.map((variable, index) => {
                const hasFileRef = !!fieldFileRefs[variable.key];
                const isFilled = !!values[variable.key]?.trim();
                return (
                  <FieldGroup key={variable.key}>
                    <FieldHeader>
                      <FieldLabel htmlFor={`field-${variable.key}`}>
                        {variable.label}
                      </FieldLabel>
                      {isFilled && <FieldCheckmark>✓</FieldCheckmark>}
                    </FieldHeader>
                    <FieldDropZone
                      $isDragOver={dragOverField === variable.key && draggingFileIndex !== null}
                      onDragEnter={(e) => handleFieldDragEnter(e, variable.key)}
                      onDragLeave={(e) => handleFieldDragLeave(e, variable.key)}
                      onDragOver={handleFieldDragOver}
                      onDrop={(e) => handleFieldDrop(e, variable.key)}
                    >
                      {hasFileRef ? (
                        <FieldFileChip>
                          <InsertDriveFileIcon />
                          <span>{fieldFileRefs[variable.key]}</span>
                          <FieldFileChipRemove
                            type="button"
                            onClick={() => handleRemoveFieldFileRef(variable.key)}
                            aria-label="Remover arquivo do campo"
                          >
                            <CloseIcon />
                          </FieldFileChipRemove>
                        </FieldFileChip>
                      ) : variable.placeholder.length > 50 ? (
                        <FieldTextArea
                          id={`field-${variable.key}`}
                          value={values[variable.key]}
                          onChange={(e) => handleInputChange(variable.key, e.target.value)}
                          placeholder={variable.placeholder}
                          rows={3}
                          autoFocus={index === 0}
                        />
                      ) : (
                        <FieldInput
                          id={`field-${variable.key}`}
                          type="text"
                          value={values[variable.key]}
                          onChange={(e) => handleInputChange(variable.key, e.target.value)}
                          placeholder={variable.placeholder}
                          autoFocus={index === 0}
                        />
                      )}
                    </FieldDropZone>
                  </FieldGroup>
                );
              })}
            </FieldsContainer>

            {/* File attachment section */}
            <FileAttachmentSection>
              <AttachButton
                type="button"
                onClick={() => fileInputRef.current?.click()}
              >
                <AttachFileIcon />
                <span>Anexar arquivo</span>
              </AttachButton>
              {/* No accept filter: an extension-based accept greys out
                  extension-less files in the OS picker (graph JSON exports etc.).
                  The backend content-validates by MIME (python-magic). */}
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
                multiple
              />
              {attachedFiles.length > 0 && (
                <AttachedFilesList>
                  {attachedFiles.map((file, index) => (
                    <AttachedFileItem
                      key={`${file.name}-${index}`}
                      draggable
                      onDragStart={(e) => handleAttachedFileDragStart(e, index)}
                      onDragEnd={handleAttachedFileDragEnd}
                      $isDragging={draggingFileIndex === index}
                    >
                      <DragHandle>
                        <DragIndicatorIcon />
                      </DragHandle>
                      <FileInfo>
                        <InsertDriveFileIcon />
                        <FileName>{file.name}</FileName>
                        <FileSize>({(file.size / 1024).toFixed(1)} KB)</FileSize>
                      </FileInfo>
                      <RemoveFileButton
                        type="button"
                        onClick={() => handleRemoveFile(index)}
                        aria-label="Remover arquivo"
                      >
                        <CloseIcon />
                      </RemoveFileButton>
                    </AttachedFileItem>
                  ))}
                </AttachedFilesList>
              )}
            </FileAttachmentSection>
          </LeftColumn>

          <RightColumn>
            <PreviewLabel>Prévia</PreviewLabel>
            <PreviewContent>
              {renderTemplatePreview()}
            </PreviewContent>
          </RightColumn>
        </FillerBody>

        <FillerFooter>
          <FooterProgress>
            <ProgressText>
              {Object.values(values).filter(v => v.trim()).length} de {prompt.variables.length} campos preenchidos
              {!allFieldsFilled && ' — campos vazios usarão valores de exemplo'}
            </ProgressText>
          </FooterProgress>
          <FooterButtons>
            <CancelButton onClick={onClose}>Cancelar</CancelButton>
            <SubmitButton
              onClick={handleSubmit}
            >
              <span>Usar</span>
              <SendIcon />
            </SubmitButton>
          </FooterButtons>
        </FillerFooter>
      </FillerContainer>
    </FillerOverlay>,
    document.body
  );
};

export default PromptFillerView;

// ============ Styled Components ============

const FillerOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: ${({ theme }) => theme.modal_overlay};
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
`;

const FillerContainer = styled.div`
  font-family: var(--font-secondary);
  position: relative;
  background: ${({ theme }) => theme.modal_background};
  color: ${({ theme }) => theme.font_color};
  border: 1px solid ${({ theme }) => theme.modal_border};
  border-radius: 16px;
  max-width: 1000px;
  width: min(95vw, 1000px);
  height: 80vh;
  max-height: 700px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
`;

const FillerHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.modal_border};
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  flex: 1;
  min-width: 0;
`;

const HeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.font_color};
  cursor: pointer;
  border-radius: 6px;
  transition: background-color 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: ${({ theme }) => theme.hover_overlay_light};
  }

  & svg {
    font-size: 1.2rem;
  }
`;

const HeaderTitle = styled.h2`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const HeaderDescription = styled.p`
  margin: 0;
  font-size: 0.8rem;
  color: ${({ theme }) => theme.text_secondary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.font_color};
  cursor: pointer;
  border-radius: 6px;
  transition: background-color 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: ${({ theme }) => theme.hover_overlay_light};
  }
`;

const FillerBody = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
  min-height: 0;
`;

const LeftColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding: 16px 20px;
  gap: 16px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.sidebar_scrollbar};
    border-radius: 3px;
  }
`;

const RightColumn = styled.div`
  width: 380px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  border-left: 1px solid ${({ theme }) => theme.modal_border};
  background: ${({ theme }) => theme.prompt_preview_bg || theme.input_surface_bg};
  overflow: hidden;

  @media (max-width: 800px) {
    display: none;
  }
`;

const PreviewLabel = styled.div`
  padding: 12px 16px;
  font-size: 0.8rem;
  font-weight: 600;
  color: ${({ theme }) => theme.text_secondary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid ${({ theme }) => theme.modal_border};
`;

const PreviewContent = styled.div`
  flex: 1;
  padding: 16px;
  overflow-y: auto;
  font-size: 0.9rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.font_color};
  white-space: pre-wrap;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.sidebar_scrollbar};
    border-radius: 3px;
  }
`;

const HighlightedVar = styled.span<{ $filled: boolean }>`
  display: inline;
  padding: 2px 6px;
  border-radius: 4px;
  background: ${({ theme, $filled }) =>
    $filled ? theme.prompt_accent_color + '20' : theme.prompt_variable_bg};
  color: ${({ theme, $filled }) =>
    $filled ? theme.font_color : theme.prompt_accent_color};
  font-weight: 500;
  border: 1px solid ${({ theme, $filled }) =>
    $filled ? 'transparent' : theme.prompt_variable_border};
  transition: all 0.2s ease;
`;

const FieldsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FieldHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const FieldLabel = styled.label`
  font-size: 0.85rem;
  font-weight: 500;
  color: ${({ theme }) => theme.font_color};
`;

const FieldCheckmark = styled.span`
  font-size: 0.75rem;
  color: #22c55e;
  font-weight: 600;
`;

const FieldDropZone = styled.div<{ $isDragOver: boolean }>`
  position: relative;
  border-radius: 8px;
  ${({ $isDragOver, theme }) =>
    $isDragOver &&
    `
    &::after {
      content: '';
      position: absolute;
      top: -2px;
      left: -2px;
      right: -2px;
      bottom: -2px;
      border: 2px solid ${theme.prompt_accent_color};
      border-radius: 10px;
      pointer-events: none;
      box-shadow: 0 0 0 3px ${theme.prompt_accent_color}33;
    }
  `}
`;

const FieldFileChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: ${({ theme }) => theme.prompt_variable_bg};
  border: 1px solid ${({ theme }) => theme.prompt_accent_color};
  border-radius: 8px;
  width: fit-content;
  max-width: 100%;

  & > svg {
    font-size: 1.1rem;
    color: ${({ theme }) => theme.prompt_accent_color};
    flex-shrink: 0;
  }

  & > span {
    font-size: 0.9rem;
    color: ${({ theme }) => theme.prompt_accent_color};
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const FieldFileChipRemove = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.text_secondary};
  cursor: pointer;
  border-radius: 4px;
  padding: 0;
  flex-shrink: 0;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.hover_overlay_light};
    color: ${({ theme }) => theme.font_color};
  }

  & svg {
    font-size: 0.9rem;
  }
`;

const FieldInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.prompt_variable_border};
  border-radius: 8px;
  font-size: 0.9rem;
  background: ${({ theme }) => theme.prompt_variable_bg};
  color: ${({ theme }) => theme.font_color};
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.prompt_accent_color};
  }

  &::placeholder {
    color: ${({ theme }) => theme.text_secondary};
    opacity: 0.7;
  }
`;

const FieldTextArea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.prompt_variable_border};
  border-radius: 8px;
  font-size: 0.9rem;
  background: ${({ theme }) => theme.prompt_variable_bg};
  color: ${({ theme }) => theme.font_color};
  resize: vertical;
  min-height: 70px;
  font-family: inherit;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.prompt_accent_color};
  }

  &::placeholder {
    color: ${({ theme }) => theme.text_secondary};
    opacity: 0.7;
  }
`;

const FileAttachmentSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 8px;
  border-top: 1px solid ${({ theme }) => theme.modal_border};
`;

const AttachButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 14px;
  border: 1px solid ${({ theme }) => theme.prompt_variable_border};
  border-radius: 9px;
  background: transparent;
  color: ${({ theme }) => theme.text_secondary};
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  width: fit-content;

  &:hover {
    border-color: ${({ theme }) => theme.prompt_accent_color};
    color: ${({ theme }) => theme.prompt_accent_color};
  }

  & svg {
    font-size: 1rem;
  }
`;

const AttachedFilesList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const AttachedFileItem = styled.div<{ $isDragging?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: ${({ theme }) => theme.prompt_variable_bg};
  border: 1px solid ${({ theme, $isDragging }) =>
    $isDragging ? theme.prompt_accent_color : theme.prompt_variable_border};
  border-radius: 6px;
  cursor: grab;
  transition: all 0.15s ease;
  opacity: ${({ $isDragging }) => ($isDragging ? 0.5 : 1)};
  max-width: 200px;

  &:hover {
    border-color: ${({ theme }) => theme.prompt_accent_color};
  }

  &:active {
    cursor: grabbing;
  }
`;

const DragHandle = styled.div`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.text_secondary};
  flex-shrink: 0;

  & svg {
    font-size: 0.9rem;
  }
`;

const FileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  overflow: hidden;
  flex: 1;
  min-width: 0;

  & svg {
    font-size: 0.9rem;
    color: ${({ theme }) => theme.prompt_accent_color};
    flex-shrink: 0;
  }
`;

const FileName = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.font_color};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FileSize = styled.span`
  font-size: 0.7rem;
  color: ${({ theme }) => theme.text_secondary};
  flex-shrink: 0;
`;

const RemoveFileButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.text_secondary};
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.15s ease;
  flex-shrink: 0;
  padding: 0;

  &:hover {
    color: #e74c3c;
  }

  & svg {
    font-size: 0.8rem;
  }
`;

const FillerFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  border-top: 1px solid ${({ theme }) => theme.modal_border};
`;

const FooterProgress = styled.div`
  display: flex;
  align-items: center;
`;

const ProgressText = styled.span`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.text_secondary};
`;

const FooterButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const CancelButton = styled.button`
  padding: 9px 18px;
  border: 1px solid ${({ theme }) => theme.modal_border};
  border-radius: 9px;
  background: ${({ theme }) => theme.hover_overlay_light};
  color: ${({ theme }) => theme.font_color};
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.hover_overlay_light};
  }
`;

const SubmitButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 20px;
  border: none;
  border-radius: 9px;
  background: #15803d;
  color: #ffffff;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    background: #166534;
  }

  &:disabled {
    background: ${({ theme }) => theme.send_button_disabled};
    cursor: not-allowed;
  }

  & svg {
    font-size: 0.95rem;
  }
`;
